/**
 * Legacy tokens from the old TF theme.
 *
 * These values are NOT part of the Studio design-system (`themes.studio`).
 * They exist here so that every hardcoded literal has a single, greppable
 * source of truth.  When design provides official replacements, update them here
 * and the change propagates everywhere.
 *
 * DO NOT add new entries — prefer `theme.palette.*` / `theme.spacing()` /
 * `theme.typography.*` for any new work.
 */

// ─── Sidebar / navigation ───────────────────────────────────────────────────

export const EDUCATION_BLUE = '#003C64';
export const SIDEBAR_WHITE = '#fffefe';
export const SIDEBAR_PROFILE_GRADIENT = '#edf8fe';
export const TOASTER_NAVY = '#001c5c';

// ─── TF blue accent ─────────────────────────────────────────────────────────

export const TF_BLUE = '#017ACA';
export const TF_BLUE_6 = '#017ACA10'; // 6% opacity
export const TF_BLUE_20 = '#017ACA33'; // 20% opacity
export const TF_BLUE_40 = '#017ACA66'; // 40% opacity
export const TF_BLUE_50 = '#017ACA80'; // 50% opacity
export const TF_BLUE_ALT = '#017ada';

// ─── Calendar booking type colors ───────────────────────────────────────────

export const GL_BOOKING_BORDER = '#41acb2';
export const GL_BOOKING_BORDER_ACTIVE = '#349ba2';
export const GL_BOOKING_BG = '#e8f5f5';
export const GL_BOOKING_BG_ACTIVE = '#c0e4e3';

export const PL_BOOKING_BORDER = '#18a6d3';
export const PL_BOOKING_BORDER_ACTIVE = '#009CCA';
export const PL_BOOKING_BG = '#e3f2f8';
export const PL_BOOKING_BG_ACTIVE = '#B7DFF0';

export const EFT_BOOKING_BG = '#d5d7f6';
export const EFT_BOOKING_BG_ACTIVE = '#aab0ed';
export const EFT_BOOKING_BORDER = '#2b39d1';

export const AXIS_BOOKING_BORDER = '#dfc427';

export const EXPIRED_BOOKING_BORDER = '#b0b8be';
export const CANCELLED_BOOKING_BG = '#eeefee';
export const CANCELLED_BOOKING_BG_ALT = '#d0d2d5';

export const SPECIAL_LESSON_PINK = '#db3389';

// ─── Greys (not in Studio palette) ──────────────────────────────────────────

export const BORDER_LIGHT = '#EFEFEF';
export const BORDER_GREY = '#d7d7d7';
export const BORDER_GREY_ALT = '#d6d6d6';
export const BORDER_MID = '#7e7e7e';
export const BORDER_MID_ALT = '#7a7a7a';

export const SLATE_GREY = '#5F6368';
export const GREY_155 = '#9B9B9B';
export const GREY_140 = '#8c8c8c';
export const GREY_199 = '#c7c7c7';
export const GREY_192 = '#c0c0c0';
export const GREY_216 = '#d8d8d8';
export const GREY_219 = '#DBDBDB';
export const GREY_213 = '#d5d9e0';
export const GREY_218 = '#dad8ca'; // warm grey (ACR legal paper border)

// ─── Background tints ───────────────────────────────────────────────────────

export const BG_VERY_LIGHT_GREY = '#f8f8f8';
export const BG_NEAR_WHITE = '#fbfbfb';
export const BG_LIGHT_GREY = '#f7f7f7';
export const BG_LIGHT_GREY_ALT = '#f5f5f5';
export const BG_LIGHT_GREY_F4 = '#f4f4f4';
export const BG_LIGHT_GREY_F0 = '#f0f0f0';
export const BG_LIGHT_GREY_F2 = '#f2f2f2'; // same as theme.palette.grey[100] but kept explicit

export const BG_LIGHT_BLUE = '#e3f7ff';
export const BG_ZIRCON = '#F3F7FF';
export const BG_AQUA_SPRING = '#EDF7FA';
export const BG_LEGAL_YELLOW = '#FFFDE6';
export const BG_SERENADE = '#fff7e8';
export const BG_PINK_LIGHT = '#ffebf6';
export const BG_PINK = '#FFE8F5';
export const BG_CLASSIC_ROSE = '#FFD3E9';
export const BG_WHITE_OVERLAY = '#FFFFFFdd';
export const BG_PINK_GLOW = '#fffafe';

// ─── Status / feedback ──────────────────────────────────────────────────────

export const WARNING_BG = '#FFE1E1';
export const ERROR_RED = '#f44336';
export const CRITICAL_RED = '#cf3131';
export const SUCCESS_GREEN = '#339885';
export const WARNING_AMBER = '#F7B124';
export const WARNING_AMBER_ALT = '#ddbe24';
export const ROLE_FOCUS_BLUE = '#9AB3FA';
export const ROLE_FOCUS_BLUE_40 = '#9AB3FA66'; // 40% opacity

// ─── Text / accent ──────────────────────────────────────────────────────────

export const HIGHLIGHT_PINK = '#FF329A';
export const HIGHLIGHT_PINK_ALT = '#FF379D';
export const TOOLTIP_BLACK = '#161313';
export const LINK_BLUE = '#0089e0';
export const LINK_BLUE_ALT = '#009eeb';
export const INFO_BLUE = '#005893';
export const ACTIVE_BLUE = '#2930d5';

// ─── Shadows / transparency ─────────────────────────────────────────────────

export const SHADOW_10 = '#1919191a'; // common.black at 10% opacity
export const SHADOW_16 = '#19191928'; // common.black at 16% opacity
export const SHADOW_20 = '#19191933'; // common.black at 20% opacity
export const SHADOW_40 = '#19191966'; // common.black at 40% opacity

// ─── Legacy spacing (off MUI spacing scale) ─────────────────────────────────
// MUI scale: theme.spacing(0–8) → [0, 4, 8, 12, 16, 24, 32, 48, 64]px
// For on-scale values, use theme.spacing(). Values below are NOT on the scale.

export const SPACING_1 = '1px';
export const SPACING_2 = '2px';
export const SPACING_3 = '3px';
export const SPACING_5 = '5px';
export const SPACING_6 = '6px';
export const SPACING_7 = '7px';
export const SPACING_9 = '9px';
export const SPACING_10 = '10px';
export const SPACING_11 = '11px';
export const SPACING_13 = '13px';
export const SPACING_14 = '14px';
export const SPACING_15 = '15px';
export const SPACING_18 = '18px';
export const SPACING_19 = '19px';
export const SPACING_20 = '20px';
export const SPACING_22 = '22px';
export const SPACING_23 = '23px';
export const SPACING_25 = '25px';
export const SPACING_26 = '26px';
export const SPACING_28 = '28px';
export const SPACING_29 = '29px';
export const SPACING_30 = '30px';
export const SPACING_33 = '33px';
export const SPACING_35 = '35px';
export const SPACING_37 = '37px';
export const SPACING_40 = '40px';
export const SPACING_42 = '42px';
export const SPACING_45 = '45px';
export const SPACING_50 = '50px';
export const SPACING_62 = '62px';
export const SPACING_75 = '75px';
export const SPACING_80 = '80px';

// ─── Legacy font sizes (off typography scale) ───────────────────────────────
// On-scale: h1=40, h2=32, h3=28, h4=24, h5=20, h6/body1=16, body2=14, SubLabel=12
// Use theme.typography.X.fontSize for on-scale values.

export const FONT_SIZE_10 = 10;
export const FONT_SIZE_13 = 13;
export const FONT_SIZE_18 = 18;
export const FONT_SIZE_48 = 48;

// ─── Legacy z-index layers ──────────────────────────────────────────────────
// These represent the app's stacking context hierarchy.
// Low values (0–2) are local stacking and don't need tokens.

export const Z_SCHEDULE_MAP_ITEM = 5;
export const Z_CALENDAR_LOADING = 9;
export const Z_LOCAL_OVERLAY = 10;
export const Z_ACR_STICKY = 40;
export const Z_CALENDAR_NAV = 50;
export const Z_TABLE_STICKY = 100;
export const Z_SCHEDULE_MAP_HEADER = 120;
export const Z_AVATAR_BADGE = 999;
export const Z_DROPDOWN = 1000;
export const Z_OVERLAY = 2000;
export const Z_OVERLAY_ABOVE = 2001;
export const Z_ACR_PANEL = 8450;
export const Z_SIDEBAR = 8500;
export const Z_MODAL_ABOVE_SIDEBAR = 8501;
export const Z_NAV_ABOVE_SIDEBAR = 8502;
export const Z_LOBBY_OVERLAY = 9000;
export const Z_POPPER_ABOVE_MODAL = 9501;
export const Z_TOASTER = 9750;
export const Z_PICKER = 9825;

// ─── Misc legacy colors ─────────────────────────────────────────────────────

export const SUCCESS_GREEN_ALT = '#3a9b78';
export const BG_LIGHT_YELLOW = '#fffce5';
export const BG_LIGHT_BLUE_F6 = '#f6f9ff';
export const BG_LIGHT_BLUE_F5 = '#f5f8ff';
export const CHECKMARK_PINK = 'rgb(255, 50, 155)';
export const ICON_DARK = '#191919';
export const ICON_LIGHT_BLUE = '#dcf4ff';
export const ICON_GREY = '#909aa3';
export const ICON_CYAN = '#64CDFF';
